import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Language, Translation } from '../types/i18n';
import { ko } from '../locales/ko';
import { en } from '../locales/en';

const STORAGE_KEY = 'rubiks-cube-language';
const LANGUAGE_SELECTED_KEY = 'rubiks-cube-language-selected';

const translations: Record<Language, Translation> = {
  ko,
  en,
};

interface LanguageContextType {
  language: Language;
  changeLanguage: (newLanguage: Language) => void;
  t: Translation;
  isFirstVisit: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [language, setLanguage] = useState<Language>('ko');
  const [isFirstVisit, setIsFirstVisit] = useState<boolean>(true);

  // localStorage에서 언어 설정 불러오기
  useEffect(() => {
    try {
      const savedLanguage = localStorage.getItem(STORAGE_KEY);
      const languageSelected = localStorage.getItem(LANGUAGE_SELECTED_KEY);

      if (savedLanguage && (savedLanguage === 'ko' || savedLanguage === 'en')) {
        setLanguage(savedLanguage);
      }

      if (languageSelected === 'true') {
        setIsFirstVisit(false);
      }
    } catch (error) {
      console.warn('Failed to load language setting from localStorage:', error);
    }
  }, []);

  // 언어 변경 함수
  const changeLanguage = (newLanguage: Language) => {
    try {
      setLanguage(newLanguage);
      localStorage.setItem(STORAGE_KEY, newLanguage);
      localStorage.setItem(LANGUAGE_SELECTED_KEY, 'true');
      setIsFirstVisit(false);
    } catch (error) {
      console.warn('Failed to save language setting to localStorage:', error);
    }
  };

  // 번역 가져오기
  const t = translations[language];

  const value = {
    language,
    changeLanguage,
    t,
    isFirstVisit,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
